import { useState, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, resolveLinkProps, hexToRgba, getGridCols, getCardRadius, getCardGap } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

const ReadingTime = ({ minutes, color }) => minutes ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: color || 'inherit', opacity: 0.7 }}>
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        {minutes} min
    </span>
) : null;

export default function BlogSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const items = rawItems.map(item => ({ ...item, ...resolveItemMedia(item, mediaMode, mediaFolders) }));
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const showReadingTime = config.show_reading_time !== false;
    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    const columns = config.columns || 3;
    const colClass = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    if (items.length === 0) return null;

    const variant = config.variant || 'grid';
    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || (s.dark ? '#111827' : '#f9fafb'), ...s.bgStyle };

    // ── Featured-hero variant ──
    if (variant === 'featured-hero') {
        const [first, ...rest] = items;
        const heroImg = first.resolvedImage || '';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {/* Hero featured post */}
                    <a {...resolveLinkProps(first.link || '#')}
                        className={`group relative block w-full rounded-2xl overflow-hidden mb-8 transition-all duration-500 hover:shadow-2xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        style={{ minHeight: 340 }}>
                        {heroImg ? (
                            <img src={heroImg} alt={first.title || ''} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy"/>
                        ) : (
                            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.7)})` }}/>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"/>
                        <div className="relative z-10 flex flex-col justify-end h-full p-6 sm:p-10" style={{ minHeight: 340 }}>
                            <div className="flex items-center gap-2 mb-2">
                                {first.tag && <span className="text-[11px] font-semibold uppercase tracking-wider text-white/70">{first.tag}</span>}
                                {first.date && <span className="text-[11px] text-white/50">{first.date}</span>}
                                {showReadingTime && first.reading_time && <ReadingTime minutes={first.reading_time} color="rgba(255,255,255,0.6)"/>}
                            </div>
                            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 group-hover:underline">{first.title}</h3>
                            {first.excerpt && <p className="text-sm text-white/80 line-clamp-2 max-w-2xl">{first.excerpt}</p>}
                            <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-white group-hover:gap-2 transition-all">
                                {content.cta_text || 'Leer mas'}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                            </span>
                        </div>
                    </a>
                    {/* Remaining posts in compact 3-col grid */}
                    {rest.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {rest.slice(0, visibleCount).map((post, i) => (
                                <a key={i} {...resolveLinkProps(post.link || '#')}
                                    className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i + 1, 10) * 100}ms` : '0ms' }}>
                                    {(post.resolvedImage || post.carouselImages || post.video) && (
                                        <div className="aspect-[16/9] overflow-hidden">
                                            <MediaRenderer item={post} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/9]" rounded="" />
                                        </div>
                                    )}
                                    <div className="p-4">
                                        <div className="flex items-center gap-2">
                                            {post.tag && <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{post.tag}</span>}
                                            {showReadingTime && post.reading_time && <ReadingTime minutes={post.reading_time} color={s.textSub}/>}
                                        </div>
                                        <h3 className="text-sm font-bold mt-1 line-clamp-2 group-hover:underline" style={{ color: s.text }}>{post.title}</h3>
                                        {post.excerpt && <p className="text-xs mt-1 line-clamp-2" style={{ color: s.textSub }}>{post.excerpt}</p>}
                                        {post.date && <p className="text-[11px] mt-2 opacity-50" style={{ color: s.textSub }}>{post.date}</p>}
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                    {visibleCount < rest.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, rest.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({rest.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Carousel variant ──
    if (variant === 'carousel') {
        const scrollRef = useRef(null);
        const scroll = useCallback((dir) => {
            if (!scrollRef.current) return;
            const cardW = scrollRef.current.querySelector('[data-blog-card]')?.offsetWidth || 320;
            scrollRef.current.scrollBy({ left: dir * (cardW + 20), behavior: 'smooth' });
        }, []);
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="relative">
                        {/* Arrow buttons */}
                        <button onClick={() => scroll(-1)}
                            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full shadow-lg transition-all hover:scale-110"
                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }}>
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                        </button>
                        <button onClick={() => scroll(1)}
                            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full shadow-lg transition-all hover:scale-110"
                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }}>
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                        </button>
                        {/* Scrollable track */}
                        <div ref={scrollRef}
                            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-1 px-1"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <style>{`.blog-carousel::-webkit-scrollbar{display:none}`}</style>
                            {items.map((post, i) => (
                                <a key={i} data-blog-card {...resolveLinkProps(post.link || '#')}
                                    className={`group ${card} overflow-hidden flex-shrink-0 snap-start transition-all duration-500 hover:-translate-y-1 blog-carousel ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ width: 'calc(33.333% - 14px)', minWidth: 280, transitionDelay: visible ? `${Math.min(i, 6) * 80}ms` : '0ms' }}>
                                    {(post.resolvedImage || post.carouselImages || post.video) && (
                                        <div className="aspect-[16/9] overflow-hidden">
                                            <MediaRenderer item={post} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/9]" rounded="" />
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-center gap-2">
                                            {post.tag && <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{post.tag}</span>}
                                            {showReadingTime && post.reading_time && <ReadingTime minutes={post.reading_time} color={s.textSub}/>}
                                        </div>
                                        <h3 className="text-base font-bold mt-1 line-clamp-2 group-hover:underline" style={{ color: s.text }}>{post.title}</h3>
                                        {post.excerpt && <p className="text-sm mt-2 line-clamp-2" style={{ color: s.textSub }}>{post.excerpt}</p>}
                                        {post.date && <p className="text-xs mt-3 opacity-50" style={{ color: s.textSub }}>{post.date}</p>}
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    if (variant === 'magazine') {
        const [first, ...rest] = items;
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    {/* Featured post */}
                    <a {...resolveLinkProps(first.link || '#')}
                        className={`group ${card} overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-0 mb-8 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                        {(first.resolvedImage || first.carouselImages || first.video) && (
                            <div className="aspect-[16/10] lg:aspect-auto overflow-hidden">
                                <MediaRenderer item={first} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/10]" rounded="" />
                            </div>
                        )}
                        <div className="p-6 flex flex-col justify-center">
                            <div className="flex items-center gap-2">
                                {first.tag && <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{first.tag}</span>}
                                {showReadingTime && first.reading_time && <ReadingTime minutes={first.reading_time} color={s.textSub}/>}
                            </div>
                            <h3 className="text-xl font-bold mt-2 group-hover:underline" style={{ color: s.text }}>{first.title}</h3>
                            {first.excerpt && <p className="text-sm mt-3 line-clamp-3" style={{ color: s.textSub }}>{first.excerpt}</p>}
                            {first.date && <p className="text-xs mt-4 opacity-50" style={{ color: s.textSub }}>{first.date}</p>}
                        </div>
                    </a>
                    {/* Rest grid */}
                    {rest.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {rest.slice(0, visibleCount).map((post, i) => (
                                <a key={i} {...resolveLinkProps(post.link || '#')}
                                    className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i + 1, 10) * 100}ms` : '0ms' }}>
                                    {(post.resolvedImage || post.carouselImages || post.video) && (
                                        <div className="aspect-[16/9] overflow-hidden">
                                            <MediaRenderer item={post} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/9]" rounded="" />
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-center gap-2">
                                            {post.tag && <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{post.tag}</span>}
                                            {showReadingTime && post.reading_time && <ReadingTime minutes={post.reading_time} color={s.textSub}/>}
                                        </div>
                                        <h3 className="text-base font-bold mt-1 line-clamp-2 group-hover:underline" style={{ color: s.text }}>{post.title}</h3>
                                        {post.excerpt && <p className="text-sm mt-2 line-clamp-2" style={{ color: s.textSub }}>{post.excerpt}</p>}
                                        {post.date && <p className="text-xs mt-3 opacity-50" style={{ color: s.textSub }}>{post.date}</p>}
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                    {visibleCount < rest.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, rest.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({rest.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="divide-y" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}>
                        {items.slice(0, visibleCount).map((post, i) => (
                            <a key={i} {...resolveLinkProps(post.link || '#')}
                                className={`group flex items-center justify-between py-4 px-3 -mx-3 rounded-lg transition-colors duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 80}ms` : '0ms' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = s.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {post.tag && <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{post.tag}</span>}
                                        {showReadingTime && post.reading_time && <ReadingTime minutes={post.reading_time} color={s.textSub}/>}
                                    </div>
                                    <h3 className="text-base font-bold group-hover:underline truncate" style={{ color: s.text }}>{post.title}</h3>
                                </div>
                                <span className="text-xs whitespace-nowrap ml-4 flex-shrink-0" style={{ color: s.textSub, opacity: 0.6 }}>{post.date}</span>
                            </a>
                        ))}
                    </div>
                    {visibleCount < items.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({items.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${getGridCols(config) || colClass} ${getCardGap(config) || 'gap-6'}`}>
                    {items.slice(0, visibleCount).map((post, i) => (
                        <a key={i} {...resolveLinkProps(post.link || '#')}
                            className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${getCardRadius(config)} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                            {(post.resolvedImage || post.carouselImages || post.video) && (
                                <div className="aspect-[16/9] overflow-hidden">
                                    <MediaRenderer item={post} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[16/9]" rounded="" />
                                </div>
                            )}
                            <div className="p-5">
                                <div className="flex items-center gap-2">
                                    {post.tag && <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: s.accent }}>{post.tag}</span>}
                                    {showReadingTime && post.reading_time && <ReadingTime minutes={post.reading_time} color={s.textSub}/>}
                                </div>
                                <h3 className="text-base font-bold mt-1 line-clamp-2 group-hover:underline" style={{ color: s.text }}>{post.title}</h3>
                                {post.excerpt && <p className="text-sm mt-2 line-clamp-2" style={{ color: s.textSub }}>{post.excerpt}</p>}
                                {post.date && <p className="text-xs mt-3 opacity-50" style={{ color: s.textSub }}>{post.date}</p>}
                            </div>
                        </a>
                    ))}
                </div>
                {visibleCount < items.length && (
                    <div className="text-center mt-8">
                        <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                            className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                            style={{borderColor: s.accent, color: s.accent}}>
                            Ver mas ({items.length - visibleCount} restantes)
                        </button>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
